const FAB_STORAGE_KEY = 'responder_messenger_fab_position';
const FAB_SIZE = 58;
const FAB_MARGIN = 12;
const PANEL_GAP = 10;
const DESKTOP_SHELL_WIDTH = 980;
const DESKTOP_SHELL_HEIGHT = 720;
const MOBILE_SHELL_WIDTH_REM = 1.2;
const MOBILE_SHELL_HEIGHT_REM = 6.1;
const DESKTOP_SHELL_WIDTH_REM = 2.4;
const DESKTOP_SHELL_HEIGHT_REM = 7.6;

const getRootRemPx = () => {
  if (typeof window === 'undefined') return 16;
  const value = Number.parseFloat(
    window.getComputedStyle(document.documentElement).fontSize || '16',
  );
  return Number.isFinite(value) ? value : 16;
};

export const getLayoutSafeInsets = () => {
  if (typeof window === 'undefined') {
    return { left: FAB_MARGIN, top: FAB_MARGIN, right: FAB_MARGIN, bottom: FAB_MARGIN };
  }

  let left = FAB_MARGIN;
  let top = FAB_MARGIN;

  const mainScrollContainer = document.getElementById('main-scroll-container');
  const siblingNav = mainScrollContainer?.previousElementSibling;
  const navRect = siblingNav?.getBoundingClientRect?.();

  if (navRect && navRect.width > 40 && navRect.right > 0) {
    left = Math.max(left, navRect.right + FAB_MARGIN);
  }

  const mobileHeader = document.getElementById('mobile-header');
  const desktopHeader = mainScrollContainer?.querySelector?.('header');
  const headerRect = (mobileHeader || desktopHeader)?.getBoundingClientRect?.();

  if (headerRect && headerRect.height > 40) {
    top = Math.max(top, headerRect.bottom + FAB_MARGIN);
  }

  return { left, top, right: FAB_MARGIN, bottom: FAB_MARGIN };
};

const getFabBounds = (safeInsets = getLayoutSafeInsets()) => {
  if (typeof window === 'undefined') {
    return {
      minX: FAB_MARGIN,
      maxX: FAB_MARGIN,
      minY: FAB_MARGIN,
      maxY: FAB_MARGIN,
    };
  }

  const minX = Math.max(FAB_MARGIN, safeInsets.left ?? FAB_MARGIN);
  const minY = Math.max(FAB_MARGIN, safeInsets.top ?? FAB_MARGIN);
  const maxX = Math.max(minX, window.innerWidth - FAB_SIZE - (safeInsets.right ?? FAB_MARGIN));
  const maxY = Math.max(minY, window.innerHeight - FAB_SIZE - (safeInsets.bottom ?? FAB_MARGIN));

  return { minX, maxX, minY, maxY };
};

export const getDefaultFabPosition = (safeInsets = getLayoutSafeInsets()) => {
  const bounds = getFabBounds(safeInsets);
  return { x: bounds.maxX, y: bounds.maxY };
};

export const clampFabPosition = (position, safeInsets = getLayoutSafeInsets()) => {
  const source = position || getDefaultFabPosition(safeInsets);
  const bounds = getFabBounds(safeInsets);

  return {
    x: Math.min(Math.max(source.x, bounds.minX), bounds.maxX),
    y: Math.min(Math.max(source.y, bounds.minY), bounds.maxY),
  };
};

export const snapFabToEdge = (position, safeInsets = getLayoutSafeInsets()) => {
  const clamped = clampFabPosition(position, safeInsets);
  const bounds = getFabBounds(safeInsets);
  const midpoint = bounds.minX + (bounds.maxX - bounds.minX) / 2;

  return {
    x: clamped.x <= midpoint ? bounds.minX : bounds.maxX,
    y: clamped.y,
  };
};

const resolveShellSize = (safeInsets = getLayoutSafeInsets()) => {
  if (typeof window === 'undefined') {
    return { width: DESKTOP_SHELL_WIDTH, height: DESKTOP_SHELL_HEIGHT };
  }

  const rem = getRootRemPx();
  const isMobile = window.innerWidth <= 992;
  const widthPadding = (isMobile ? MOBILE_SHELL_WIDTH_REM : DESKTOP_SHELL_WIDTH_REM) * rem;
  const heightPadding = (isMobile ? MOBILE_SHELL_HEIGHT_REM : DESKTOP_SHELL_HEIGHT_REM) * rem;
  const availableWidth =
    window.innerWidth - (safeInsets.left ?? FAB_MARGIN) - (safeInsets.right ?? FAB_MARGIN);
  const availableHeight =
    window.innerHeight - (safeInsets.top ?? FAB_MARGIN) - (safeInsets.bottom ?? FAB_MARGIN);

  return {
    width: Math.max(360, Math.min(DESKTOP_SHELL_WIDTH, availableWidth - widthPadding)),
    height: Math.max(420, Math.min(DESKTOP_SHELL_HEIGHT, availableHeight - heightPadding)),
  };
};

export const resolveShellPositionFromFab = (fabPosition, safeInsets = getLayoutSafeInsets()) => {
  if (typeof window === 'undefined') {
    return {
      left: FAB_MARGIN,
      top: FAB_MARGIN,
      width: DESKTOP_SHELL_WIDTH,
      height: DESKTOP_SHELL_HEIGHT,
    };
  }

  const clampedFab = clampFabPosition(fabPosition, safeInsets);
  const size = resolveShellSize(safeInsets);
  const minLeft = Math.max(FAB_MARGIN, safeInsets.left ?? FAB_MARGIN);
  const minTop = Math.max(FAB_MARGIN, safeInsets.top ?? FAB_MARGIN);
  const maxLeft = Math.max(minLeft, window.innerWidth - (safeInsets.right ?? FAB_MARGIN) - size.width);
  const maxTop = Math.max(minTop, window.innerHeight - (safeInsets.bottom ?? FAB_MARGIN) - size.height);
  const midpoint = minLeft + (maxLeft - minLeft) / 2;
  const preferRightAligned = clampedFab.x > midpoint;

  let left = preferRightAligned ? clampedFab.x + FAB_SIZE - size.width : clampedFab.x;
  let top = clampedFab.y - size.height - PANEL_GAP;

  if (top < minTop) {
    top = clampedFab.y + FAB_SIZE + PANEL_GAP;
  }

  if (top > maxTop) top = maxTop;
  if (left < minLeft) left = minLeft;
  if (left > maxLeft) left = maxLeft;

  return { left, top, width: size.width, height: size.height };
};

export const loadStoredFabPosition = () => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(FAB_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const x = Number(parsed?.x);
    const y = Number(parsed?.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y };
  } catch {
    return null;
  }
};

export { FAB_STORAGE_KEY };
